/**
 * Hook: polls /api/admin/orders every 25s and triggers a browser notification
 * + a short audio beep when a new paid order arrives.
 *
 * Uses localStorage to remember the last-seen order id across sessions/tabs.
 * No external dependencies, no service worker required (only fires while the
 * admin tab is open — acceptable for an internal panel).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'jabad_admin_last_seen_order';
const POLL_MS = 25000;

const playBeep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.6);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.65);
    // second tone for distinctiveness
    setTimeout(() => {
      const o2 = ctx.createOscillator();
      const g2 = ctx.createGain();
      o2.connect(g2);
      g2.connect(ctx.destination);
      o2.frequency.value = 1320;
      g2.gain.setValueAtTime(0.0001, ctx.currentTime);
      g2.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
      g2.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.5);
      o2.start(ctx.currentTime);
      o2.stop(ctx.currentTime + 0.55);
    }, 250);
  } catch {
    // Web Audio unavailable; silent fallback
  }
};

export const supportsNotifications = () =>
  typeof window !== 'undefined' && 'Notification' in window;

export const useNewOrderNotifications = ({ authHeader, enabled }) => {
  const [permission, setPermission] = useState(
    supportsNotifications() ? Notification.permission : 'denied'
  );
  const [newCount, setNewCount] = useState(0);
  const lastSeenRef = useRef(localStorage.getItem(STORAGE_KEY) || null);

  const requestPermission = useCallback(async () => {
    if (!supportsNotifications()) return 'denied';
    if (Notification.permission === 'granted' || Notification.permission === 'denied') {
      setPermission(Notification.permission);
      return Notification.permission;
    }
    const result = await Notification.requestPermission();
    setPermission(result);
    return result;
  }, []);

  const markAllSeen = useCallback((latestId) => {
    if (latestId) {
      localStorage.setItem(STORAGE_KEY, latestId);
      lastSeenRef.current = latestId;
    }
    setNewCount(0);
  }, []);

  useEffect(() => {
    if (!enabled || !authHeader) return undefined;
    let cancelled = false;
    let firstRun = true;

    const fire = (order) => {
      if (permission === 'granted') {
        try {
          const n = new Notification('Nuevo pedido recibido', {
            body: `${order.customer_name || 'Cliente'} — $${(order.total_amount || 0).toFixed(2)} ${order.payment_currency || 'MXN'}`,
            tag: order.id,
            requireInteraction: false,
          });
          n.onclick = () => {
            window.focus();
            n.close();
          };
        } catch {
          /* ignore */
        }
      }
      playBeep();
    };

    const poll = async () => {
      try {
        const res = await axios.get(`${API}/admin/orders`, {
          headers: { Authorization: `Bearer ${authHeader}` },
        });
        if (cancelled) return;
        const orders = res.data || [];
        if (orders.length === 0) return;

        const latestId = orders[0].id;
        const lastSeen = lastSeenRef.current;

        if (firstRun) {
          // On first run, just record the current latest without notifying
          if (!lastSeen) {
            localStorage.setItem(STORAGE_KEY, latestId);
            lastSeenRef.current = latestId;
          }
          firstRun = false;
          return;
        }

        if (latestId !== lastSeen) {
          // Count how many are newer than lastSeen
          let newOnes = orders;
          if (lastSeen) {
            const idx = orders.findIndex((o) => o.id === lastSeen);
            newOnes = idx >= 0 ? orders.slice(0, idx) : orders;
          }
          if (newOnes.length > 0) {
            setNewCount((c) => c + newOnes.length);
            // Fire notification for the most recent one only (avoid spam)
            fire(newOnes[0]);
            localStorage.setItem(STORAGE_KEY, latestId);
            lastSeenRef.current = latestId;
          }
        }
      } catch (e) {
        console.warn('[new-order-polling] fetch failed:', e?.message || e);
      }
    };

    poll();
    const t = setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [authHeader, enabled, permission]);

  return { permission, requestPermission, newCount, markAllSeen };
};
